import { useCallback, useEffect, useMemo, useState } from "react";
import { PolicySaysChip } from "../components/PolicySaysChip";
import { useSearchParams } from "react-router-dom";
import { useDeepLinkScroll } from "../hooks/useDeepLinkScroll";
import { Plus, Target } from "lucide-react";
import { theme, card, btnPrimary, btnSecondary, type as text, pageHeading, pageSubtitle } from "../theme";
import { api, apiErrorMessage } from "../api/client";
import { LoadingBlock } from "../components/Spinner";
import { ControlPicker } from "../components/ControlPicker";
import { EntityTagsRow, EntityTagFilters, useEntityTagData, useTagFilter, matchesEntityTagFilter, entityRef, KIND_HREFS } from "../components/EntityTags";
import { RelatedRecords, riskLabel, type RelatedLink } from "../components/RelatedRecords";
import { NextSteps, type NextStepItem } from "../components/NextSteps";
import { RegisterToolbar, SummaryTiles } from "../components/RegisterToolbar";
import { downloadCsv, matchesSearch, by, type SortOption, type ExportColumn } from "../lib/registerExport";
import { ImprovementBoard } from "../components/ImprovementBoard";
import { ActionRootCause, RootCauseView } from "../components/ActionRootCause";
import { RootCauseDraftPanel } from "../components/RootCauseDraft";
import { ActionEffectiveness, EffectivenessView, hasEffectivenessReview, canRecordEffectiveness, type EffectivenessStanding } from "../components/ActionEffectiveness";
import { DetailFields, DetailSection, EditButton, RecordActions } from "../components/RecordView";
import { useCurrentUser } from "../auth/useCurrentUser";
import { canWriteAction, canWriteAnyAction } from "../lib/permissions";
import { TicketChip, TicketControl, useTicketLinks } from "../components/TicketLink";
import {
  EmptyState, Field, ModalOverlay, ModalHeader, StatusPill, input, textarea, secondaryBtn, dangerBtn,
  toDateInput, fromInput, fmtDay, fmtDateOnly,
} from "./resilience/shared";

// The improvement register: corrective actions and the programmes they roll up into.
//
// The list leads with health rather than status, because "overdue" and "blocked" are what a
// weekly review acts on, while "approved" is only where the paperwork got to. Rules and the state
// machine are server-side in backend/src/lib/correctiveActions.ts; the board offers only the moves
// GET /improvement/catalogue says the machine allows.

type Health = "on_track" | "due_soon" | "overdue" | "blocked" | "unowned" | "closed";

const HEALTH: Record<Health, { label: string; color: string; bg: string }> = {
  // Theme tokens, never literals: the palette is swapped per scheme by ThemeContext
  // (dark / light / colour-blind), so a hex here opts the badge out of all three.
  // "Blocked" and "No owner" both warn; they keep different surfaces (warning vs the
  // medium risk band) so the two are still told apart without relying on hue alone.
  overdue:   { label: "Overdue",   color: theme.color.dangerText,   bg: theme.color.dangerBg },
  blocked:   { label: "Blocked",   color: theme.color.warningText,  bg: theme.color.warningBg },
  unowned:   { label: "No owner",  color: theme.color.riskMedText,  bg: theme.color.riskMedBg },
  due_soon:  { label: "Due soon",  color: theme.color.accentText,   bg: theme.color.accentBg },
  on_track:  { label: "On track",  color: theme.color.successText,  bg: theme.color.successBg },
  closed:    { label: "Closed",    color: theme.color.muted,        bg: theme.color.panelAlt },
};

const PRIORITIES = ["critical", "high", "medium", "low"] as const;
const DAY = 86_400_000;
/** Open longer than this counts as ageing. */
const AGED_DAYS = 90;

interface Catalogue {
  statuses: Array<{ id: string; label: string }>;
  transitions: Record<string, string[]>;
  sources: Array<{ id: string; label: string }>;
  rootCauseMethods: Array<{ id: string; label: string }>;
  rootCauseRequiredSources: string[];
  effectivenessReviewOptions: number[];
  defaultEffectivenessDays: number;
}

// Used only until the catalogue answers, so the page never renders with no statuses at all.
const FALLBACK_CATALOGUE: Catalogue = {
  statuses: ["proposed", "approved", "in_progress", "blocked", "completed", "verified", "cancelled"].map((id) => ({ id, label: id.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase()) })),
  transitions: {},
  sources: [],
  rootCauseMethods: [],
  rootCauseRequiredSources: [],
  effectivenessReviewOptions: [90],
  defaultEffectivenessDays: 90,
};

interface Milestone {
  id: string; title: string; dueDate: string | null; completedAt: string | null;
  // The member who carries out the step (on their My tasks), or the name as a recommendation wrote it.
  ownerUserId?: string | null; ownerName?: string | null; owner?: { id: string; displayName: string | null; email: string } | null;
}
interface Action {
  id: string; displayId: number | null; title: string; description: string | null;
  source: string; sourceRef: string | null; controlId: string | null; frameworkId: string | null;
  priority: string; status: string; ownerUserId: string | null; ownerName: string | null; dueDate: string | null;
  startedAt: string | null; completedAt: string | null; verifiedAt: string | null; createdAt: string;
  blockedReason: string | null; effortDays: number | null; programmeId: string | null;
  verificationNote?: string | null;
  rootCauseMethod: string | null; rootCause: string | null; rootCauseRequired: boolean;
  /** When the saved root cause started from an AI draft the owner reviewed (null = written from scratch). */
  rootCauseAiDraftAt?: string | null;
  effectivenessDueAt: string | null; effectivenessReviewedAt: string | null;
  effectivenessResult: string | null; effectivenessNote: string | null;
  effectiveness: EffectivenessStanding;
  milestones: Milestone[]; health: Health;
  // The register row the action fixes, where it fixes one (sourceRef stays free
  // text for an attention item or a finding the platform does not own).
  risk: { id: string; displayId: number | null; title: string; status: string } | null;
  incident: { id: string; displayId: string | null; title: string; severity: string } | null;
  supplier: { id: string; name: string; riskTier: string } | null;
  asset: { id: string; name: string; kind: string; criticality: string } | null;
  // Every control it serves beyond controlId, and the auditor findings it fixes (backend lib/findingActions.ts).
  controls?: Array<{ controlId: string; frameworkId: string | null }>;
  auditorFindings?: Array<{ id: string; controlId: string; status: string; clientStatus: string | null; bureau: { name: string } | null }>;
  // Raised by the platform from a failure it can see; autoResolvedAt = completed because the failure cleared.
  automatic?: boolean;
  autoResolvedAt?: string | null;
}
interface AutomaticSource { id: string; label: string; description: string; enabled: boolean }
interface ProgrammeHealth {
  total: number; open: number; completed: number; verified: number; overdue: number;
  blocked: number; unowned: number; completionPct: number; elapsedPct: number | null;
  behindSchedule: boolean; effortDaysRemaining: number;
}
interface Programme {
  id: string; name: string; objective: string | null; sponsorName: string | null; status: string;
  startDate: string | null; endDate: string | null; targetFrameworkId: string | null;
  targetLevel: number | null; notes?: string | null; health: ProgrammeHealth;
}

/** Mirrors PROGRAMME_STATUSES in backend/src/lib/correctiveActions.ts. */
const PROGRAMME_STATUSES = ["planned", "active", "paused", "complete", "cancelled"] as const;

import { sourceRefLabel } from "../lib/sourceRef";
const words = (s: string) => s.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
const ref = (a: { displayId: number | null }) => (a.displayId !== null ? `CA-${a.displayId}` : "");
const ageDays = (a: Action) => Math.max(0, Math.floor((Date.now() - new Date(a.createdAt).getTime()) / DAY));
const isOpen = (a: Action) => a.health !== "closed";
const reviewDue = (a: Action) => a.effectiveness === "due" || a.effectiveness === "overdue";

function actionRecordLinks(a: Action): RelatedLink[] {
  return [
    ...(a.risk ? [{ kindLabel: "Risk", label: riskLabel(a.risk), href: KIND_HREFS.risk(a.risk.id) }] : []),
    ...(a.incident ? [{ kindLabel: "Incident", label: `${a.incident.displayId != null ? `INC-${String(a.incident.displayId).padStart(3, "0")} ` : ""}${a.incident.title}`, href: KIND_HREFS.incident(a.incident.id) }] : []),
    ...(a.supplier ? [{ kindLabel: "Supplier", label: a.supplier.name, href: KIND_HREFS.supplier(a.supplier.id) }] : []),
    ...(a.asset ? [{ kindLabel: "Asset", label: `${a.asset.name} (${a.asset.criticality})`, href: KIND_HREFS.asset(a.asset.id) }] : []),
    ...(a.controlId ? [{ kindLabel: "Control", label: a.controlId, href: null }] : []),
    ...(a.controls ?? []).filter((c) => c.controlId !== a.controlId).map((c) => ({ kindLabel: "Control", label: c.controlId, href: null })),
    ...(a.auditorFindings ?? []).map((f) => ({
      kindLabel: "Auditor finding",
      label: `${f.controlId}${f.bureau ? ` (${f.bureau.name})` : ""} — ${f.status === "resolved" ? "approved by the auditor" : f.clientStatus === "resolved" ? "closure proposed to the auditor" : "open"}`,
      href: `/auditor-feedback?finding=${f.id}`,
    })),
    ...(a.sourceRef?.startsWith("ca-followup:") ? [{ kindLabel: "Follow-up to", label: "The action that did not work", href: `/improvement?action=${a.sourceRef.slice("ca-followup:".length)}` }] : []),
  ];
}

type Form = {
  title: string; description: string; source: string; priority: string; status: string;
  ownerUserId: string; ownerName: string; dueDate: string; effortDays: string; programmeId: string;
  // The standard is carried beside the control because the two must agree; the
  // server takes it from the control it is given rather than trusting this.
  controlId: string; frameworkId: string; blockedReason: string; verificationNote: string;
  // The register row the action fixes (checked against this organisation server-side).
  riskId: string; incidentId: string; supplierId: string; assetId: string;
  rootCauseMethod: string; rootCause: string;
  /** The root cause in the editor came from an AI draft in this edit; saving records it. */
  rootCauseFromAiDraft: boolean;
  /** How long after verification to review effectiveness; read on the move to verified. */
  effectivenessReviewDays: string;
};

interface LinkOption { id: string; name: string }
interface LinkLookups { risks: LinkOption[]; incidents: LinkOption[]; suppliers: LinkOption[]; assets: LinkOption[]; members: LinkOption[] }

/**
 * What an action still needs before it can close, in the order a weekly review
 * would chase it. Nothing counts as done by completion alone: 10.2 asks for the
 * cause to be found, the fix to be checked by someone else, and later for its
 * effectiveness to be reviewed, so each is its own line.
 */
function actionSteps(a: Action, f: Form, sourceLabel: string): NextStepItem[] {
  if (a.status === "cancelled") return [];
  const overdue = !!f.dueDate && new Date(f.dueDate).getTime() < Date.now() && !["completed", "verified"].includes(a.status);
  const closed = a.status === "completed" || a.status === "verified";
  const hasCause = !!f.rootCause.trim();
  return [
    { done: !!f.ownerName.trim(), label: "Name an owner", hint: "An action nobody owns will not move.", urgent: a.health === "unowned" },
    { done: !!f.dueDate && !overdue, label: f.dueDate ? "Finish it, or agree a new date" : "Set a due date", hint: overdue ? `It was due ${fmtDateOnly(f.dueDate)}.` : undefined, urgent: overdue },
    ...(a.rootCauseRequired ? [{
      done: hasCause, label: "Record the root cause",
      hint: `Required before it can be completed: it was raised from ${sourceLabel.toLowerCase()} (ISO 27001 10.2 b).`,
      urgent: !hasCause && (a.status === "in_progress" || a.status === "blocked"),
    }] : []),
    { done: !!f.controlId, label: "Link the control or clause it serves", hint: "So the SOA and the evidence pack line up with it." },
    { done: !!(f.riskId || f.incidentId || f.supplierId || f.assetId) || a.source === "soa_gap" || a.source === "maturity", label: "Link the register row it fixes", hint: "The risk, incident, supplier or asset that raised it." },
    ...(a.status === "blocked" ? [{ done: !!f.blockedReason.trim(), label: "Say why it is blocked", urgent: !f.blockedReason.trim() }] : []),
    ...(closed ? [
      { done: a.status === "verified", label: "Have someone other than the owner verify it worked", hint: "Set the status to Verified and say how it was checked (ISO 27001 10.2)." },
      { done: a.status !== "verified" || !!(a.verificationNote ?? f.verificationNote), label: "Record the evidence it worked" },
    ] : []),
    ...(a.status === "verified" ? [{
      done: a.effectiveness === "reviewed", label: "Review whether the fix held",
      hint: a.effectivenessDueAt ? `Effectiveness review ${a.effectiveness === "overdue" ? "was due" : "due"} ${fmtDay(a.effectivenessDueAt)} (ISO 27001 10.2 d).` : "ISO 27001 10.2 d.",
      urgent: a.effectiveness === "overdue",
    }] : []),
  ];
}
const EMPTY: Form = {
  title: "", description: "", source: "manual", priority: "medium", status: "proposed",
  ownerUserId: "", ownerName: "", dueDate: "", effortDays: "", programmeId: "", controlId: "", frameworkId: "",
  blockedReason: "", verificationNote: "",
  riskId: "", incidentId: "", supplierId: "", assetId: "",
  rootCauseMethod: "", rootCause: "", rootCauseFromAiDraft: false, effectivenessReviewDays: "",
};

type View = "attention" | "open" | "all" | "overdue" | "verification" | "effectiveness" | "blocked" | "aged";

function inView(a: Action, view: View): boolean {
  switch (view) {
    case "all": return true;
    case "open": return isOpen(a);
    case "attention": return ["overdue", "blocked", "unowned", "due_soon"].includes(a.health);
    case "overdue": return a.health === "overdue";
    case "verification": return a.status === "completed";
    case "effectiveness": return reviewDue(a);
    case "blocked": return a.status === "blocked";
    case "aged": return isOpen(a) && ageDays(a) > AGED_DAYS;
  }
}

const STATUS_ORDER = ["blocked", "in_progress", "approved", "proposed", "completed", "verified", "cancelled"];

const SORTS: SortOption<Action>[] = [
  { key: "due", label: "Due date", compare: by.date<Action>((a) => a.dueDate, "asc") },
  { key: "priority", label: "Priority", compare: by.rank<Action>((a) => a.priority, PRIORITIES) },
  { key: "age", label: "Oldest first", compare: by.number<Action>(ageDays, "desc") },
  { key: "status", label: "Status", compare: by.rank<Action>((a) => a.status, STATUS_ORDER) },
  { key: "ref", label: "Reference", compare: by.number<Action>((a) => a.displayId, "asc") },
];

const LAYOUT_KEY = "improvement.layout";
const readLayout = (): "list" | "board" => {
  try { return localStorage.getItem(LAYOUT_KEY) === "board" ? "board" : "list"; } catch { return "list"; }
};

export function ImprovementRegisterPage() {
  // Who may write an action depends on what it fixes (lib/permissions.ts, mirroring the API):
  // controls:write any, a Risk Manager a risk treatment, an Internal Auditor an internal audit
  // finding, and so on. Without any of them the page is the read-only view alone.
  const { user } = useCurrentUser();
  const perms = user?.permissions;
  const canEdit = canWriteAnyAction(perms);
  // Programmes are planned by whoever holds controls:write (the server's requirePermission).
  const canPlan = !!perms?.includes("controls:write");
  // Tickets raised in the organisation's ticketing system, shown beside the platform's status.
  const tickets = useTicketLinks("corrective_action");
  // The programme open in its form: null = closed, { programme: null } = a new one.
  const [programmeForm, setProgrammeForm] = useState<{ programme: Programme | null } | null>(null);
  const [actions, setActions] = useState<Action[] | null>(null);
  // The action open in its read-only view; read from the list so a save shows the new state.
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [recordingReview, setRecordingReview] = useState(false);
  const [programmes, setProgrammes] = useState<Programme[]>([]);
  const [catalogue, setCatalogue] = useState<Catalogue>(FALLBACK_CATALOGUE);
  const [view, setView] = useState<View>("attention");
  const [layout, setLayoutState] = useState<"list" | "board">(readLayout);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("due");
  const [editing, setEditing] = useState<{ action: Action | null } | null>(null);
  const [form, setForm] = useState<Form>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [lookups, setLookups] = useState<LinkLookups | null>(null);
  // ?action=<id>: a cross-reference elsewhere linked straight to one action.
  // ?health=<h>: an attention roll-up linked to the filtered list.
  const [searchParams, setSearchParams] = useSearchParams();
  const actionParam = searchParams.get("action");
  const healthParam = searchParams.get("health");
  // ?programme=<id>: the attention list linked to one programme's card.
  useDeepLinkScroll(searchParams.get("programme"), programmes.length > 0);

  const setLayout = (l: "list" | "board") => {
    setLayoutState(l);
    try { localStorage.setItem(LAYOUT_KEY, l); } catch { /* per-viewer convenience only */ }
  };

  const load = useCallback(async () => {
    try {
      const [a, p, c] = await Promise.all([
        api.get<{ actions: Action[] }>("/improvement/actions"),
        api.get<{ programmes: Programme[] }>("/improvement/programmes"),
        api.get<Catalogue>("/improvement/catalogue").catch(() => null),
      ]);
      setActions(a.actions);
      setProgrammes(p.programmes);
      if (c) setCatalogue(c);
    } catch (e) {
      setError(apiErrorMessage(e, "Could not load the improvement register."));
      setActions([]);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    if (!actionParam || !actions) return;
    const hit = actions.find((a) => a.id === actionParam);
    if (hit) show(hit);
    const next = new URLSearchParams(searchParams); next.delete("action"); setSearchParams(next, { replace: true });
  }, [actionParam, actions]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!healthParam) return;
    setView(healthParam === "overdue" ? "overdue" : healthParam === "blocked" ? "blocked" : "attention");
    const next = new URLSearchParams(searchParams); next.delete("health"); setSearchParams(next, { replace: true });
  }, [healthParam]); // eslint-disable-line react-hooks/exhaustive-deps
  // The link pickers' options, read once when the form first opens.
  useEffect(() => {
    if (!editing || lookups) return;
    (async () => {
      const [risks, incidents, suppliers, assets, members] = await Promise.all([
        api.get<{ risks: Array<{ id: string; title: string; displayId: number | null }> }>("/risks").then((d) => d.risks.map((r) => ({ id: r.id, name: riskLabel(r) }))).catch(() => []),
        api.get<{ incidents: Array<{ id: string; title: string; displayId: number | null }> }>("/resilience/incidents").then((d) => d.incidents.map((i) => ({ id: i.id, name: `${i.displayId != null ? `INC-${String(i.displayId).padStart(3, "0")} ` : ""}${i.title}` }))).catch(() => []),
        api.get<{ suppliers: Array<{ id: string; name: string }> }>("/tprm/suppliers").then((d) => d.suppliers.map((s) => ({ id: s.id, name: s.name }))).catch(() => []),
        api.get<{ assets: Array<{ id: string; name: string }> }>("/resilience/assets").then((d) => d.assets.map((a) => ({ id: a.id, name: a.name }))).catch(() => []),
        api.get<{ members: Array<{ id: string; displayName: string | null; email: string }> }>("/organizations/members").then((d) => d.members.map((m) => ({ id: m.id, name: m.displayName || m.email }))).catch(() => []),
      ]);
      setLookups({ risks, incidents, suppliers, assets, members });
    })();
  }, [editing, lookups]);

  const tagData = useEntityTagData();
  const [tagFilter, setTagFilter] = useTagFilter();
  const sourceLabel = useCallback((id: string) => catalogue.sources.find((s) => s.id === id)?.label ?? words(id), [catalogue]);

  const shown = useMemo(() => {
    if (!actions) return [];
    const rows = actions.filter((a) =>
      inView(a, view)
      && matchesEntityTagFilter(tagData, entityRef({ kind: "action", id: a.id }), tagFilter)
      && matchesSearch(search, a.title, a.description, ref(a), a.ownerName, sourceLabel(a.source), a.controlId, a.rootCause, a.sourceRef));
    const cmp = SORTS.find((s) => s.key === sort)?.compare;
    return cmp ? [...rows].sort(cmp) : rows;
  }, [actions, view, tagData, tagFilter, search, sort, sourceLabel]);

  function formOf(action: Action, preset?: { status: string }): Form {
    return {
      title: action.title, description: action.description ?? "", source: action.source,
      priority: action.priority, status: preset?.status ?? action.status,
      ownerUserId: action.ownerUserId ?? "", ownerName: action.ownerName ?? "",
      dueDate: toDateInput(action.dueDate), effortDays: action.effortDays?.toString() ?? "",
      programmeId: action.programmeId ?? "", controlId: action.controlId ?? "", frameworkId: action.frameworkId ?? "",
      blockedReason: action.blockedReason ?? "", verificationNote: action.verificationNote ?? "",
      riskId: action.risk?.id ?? "", incidentId: action.incident?.id ?? "", supplierId: action.supplier?.id ?? "", assetId: action.asset?.id ?? "",
      rootCauseMethod: action.rootCauseMethod ?? "", rootCause: action.rootCause ?? "", rootCauseFromAiDraft: false,
      effectivenessReviewDays: String(catalogue.defaultEffectivenessDays),
    };
  }

  /** Open an action's read-only view (a row, a card, a deep link). */
  function show(action: Action) {
    setViewingId(action.id);
    setRecordingReview(false);
    setError("");
    setNotice("");
  }

  /** Open the edit form (from the view's Edit, or a move that needs something typed) or the raise form. */
  function open(action: Action | null, preset?: { status: string }) {
    // A new action starts from the first source this member may raise, so the form never
    // opens on one the server will refuse.
    const firstSource = catalogue.sources.find((src) => canWriteAction(perms, { source: src.id }))?.id ?? EMPTY.source;
    setForm(action ? formOf(action, preset) : { ...EMPTY, source: canWriteAction(perms, { source: EMPTY.source }) ? EMPTY.source : firstSource });
    setError("");
    setNotice("");
    if (action) setViewingId(action.id);
    setEditing({ action });
  }

  async function save() {
    if (!form.title.trim()) { setError("A title is required."); return; }
    const existing = editing?.action ?? null;
    if (existing && form.status === "completed" && existing.status !== "completed"
      && catalogue.rootCauseRequiredSources.includes(form.source) && !form.rootCause.trim()) {
      setError("Record the root cause before completing this action.");
      return;
    }
    setSaving(true); setError("");
    const body = {
      title: form.title.trim(),
      description: form.description.trim() || null,
      source: form.source,
      priority: form.priority,
      ownerUserId: form.ownerUserId || null,
      ownerName: form.ownerName.trim() || null,
      dueDate: fromInput(form.dueDate),
      effortDays: form.effortDays ? Number(form.effortDays) : null,
      programmeId: form.programmeId || null,
      // The standard is not sent: the server takes it from the control.
      controlId: form.controlId.trim() || null,
      riskId: form.riskId || null, incidentId: form.incidentId || null, supplierId: form.supplierId || null, assetId: form.assetId || null,
      rootCauseMethod: form.rootCauseMethod || null,
      rootCause: form.rootCause.trim() || null,
      // Saving a root cause that started from the AI draft records that it did (the server stamps it).
      ...(form.rootCauseFromAiDraft && form.rootCause.trim() ? { rootCauseFromAiDraft: true } : {}),
      ...(existing ? {
        status: form.status, blockedReason: form.blockedReason.trim() || null, verificationNote: form.verificationNote.trim() || null,
        ...(form.status === "verified" && existing.status !== "verified" && form.effectivenessReviewDays ? { effectivenessReviewDays: Number(form.effectivenessReviewDays) } : {}),
      } : {}),
    };
    try {
      // Back to the record's view, not the list, so the saved state is what is shown next.
      if (existing) await api.put(`/improvement/actions/${existing.id}`, body);
      else {
        const created = await api.post<{ action?: { id: string } }>("/improvement/actions", body);
        setViewingId(created?.action?.id ?? null);
      }
      setEditing(null);
      await load();
    } catch (e) {
      setError(apiErrorMessage(e, "Could not save the action."));
    } finally {
      setSaving(false);
    }
  }

  /**
   * A move from the board. The moves that need something typed — a reason for blocking, how it
   * was verified, a missing root cause — open the form with the status already chosen rather
   * than failing; every other move goes straight to the same PUT the form uses.
   */
  async function move(a: Action, to: string) {
    const needsCause = to === "completed" && a.rootCauseRequired && !a.rootCause?.trim();
    if (to === "blocked" || to === "verified" || needsCause) {
      open(a, { status: to });
      if (needsCause) setError("Record the root cause before completing this action.");
      return;
    }
    setBusyId(a.id); setError("");
    try {
      await api.put(`/improvement/actions/${a.id}`, { status: to });
      await load();
    } catch (e) {
      setError(apiErrorMessage(e, "Could not move the action."));
    } finally {
      setBusyId(null);
    }
  }

  async function remove(action: Action) {
    setError("");
    try {
      await api.del(`/improvement/actions/${action.id}`);
      setEditing(null);
      setViewingId(null);
      await load();
    } catch (e) {
      setError(apiErrorMessage(e, "Could not delete the action."));
    }
  }

  function exportCsv() {
    const columns: ExportColumn<Action>[] = [
      { header: "Reference", value: ref },
      { header: "Title", value: (a) => a.title },
      { header: "Status", value: (a) => words(a.status) },
      { header: "Health", value: (a) => HEALTH[a.health]?.label ?? a.health },
      { header: "Priority", value: (a) => words(a.priority) },
      { header: "Raised by", value: (a) => sourceLabel(a.source) },
      { header: "Owner", value: (a) => a.ownerName },
      { header: "Due", value: (a) => a.dueDate?.slice(0, 10) },
      { header: "Raised", value: (a) => a.createdAt.slice(0, 10) },
      { header: "Age (days)", value: (a) => (isOpen(a) ? ageDays(a) : null) },
      { header: "Completed", value: (a) => a.completedAt?.slice(0, 10) },
      { header: "Verified", value: (a) => a.verifiedAt?.slice(0, 10) },
      { header: "Root cause method", value: (a) => catalogue.rootCauseMethods.find((m) => m.id === a.rootCauseMethod)?.label ?? a.rootCauseMethod },
      { header: "Root cause", value: (a) => a.rootCause },
      { header: "Effectiveness review due", value: (a) => a.effectivenessDueAt?.slice(0, 10) },
      { header: "Effectiveness result", value: (a) => (a.effectivenessResult ? words(a.effectivenessResult) : null) },
      { header: "Control", value: (a) => a.controlId },
    ];
    downloadCsv("corrective-actions", columns, shown);
  }

  if (actions === null) return <LoadingBlock />;

  const open_ = actions.filter(isOpen);
  const ages = open_.map(ageDays).sort((a, b) => a - b);
  const median = ages.length === 0 ? 0 : ages.length % 2 ? ages[(ages.length - 1) / 2] : Math.round((ages[ages.length / 2 - 1] + ages[ages.length / 2]) / 2);
  const counts = {
    overdue: actions.filter((a) => a.health === "overdue").length,
    blocked: actions.filter((a) => a.status === "blocked").length,
    unowned: actions.filter((a) => a.health === "unowned").length,
    // The same predicate the "Needs attention" list filters by, so the count is what it shows.
    attention: actions.filter((a) => inView(a, "attention")).length,
    open: open_.length,
    verification: actions.filter((a) => a.status === "completed").length,
    effectiveness: actions.filter(reviewDue).length,
    aged: open_.filter((a) => ageDays(a) > AGED_DAYS).length,
  };
  const tile = (key: View) => ({ onClick: () => setView(view === key ? "open" : key), active: view === key });
  const current = editing?.action ?? null;
  const viewing = !editing && viewingId ? actions.find((a) => a.id === viewingId) ?? null : null;
  const modalOpen = !!editing || !!viewing;
  const statusOptions = current
    ? [current.status, ...(catalogue.transitions[current.status] ?? catalogue.statuses.map((s) => s.id).filter((s) => s !== current.status))]
    : [];

  const healthBadge = (a: Action) => (
    <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 4, background: HEALTH[a.health].bg, color: HEALTH[a.health].color }}>
      {HEALTH[a.health].label}
    </span>
  );

  return (
    <div className="page-pad">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, gap: 16, flexWrap: "wrap" }}>
        <div>
          <h1 style={pageHeading}>Improvement register</h1>
          <p style={{ ...pageSubtitle, maxWidth: 640 }}>
            Every corrective action in one place, whatever raised it — a gap, a finding, an incident
            lesson, a maturity shortfall. Verification is a separate step from completion, because the
            person who does the work is rarely the person who should confirm it worked.
          </p>
          <PolicySaysChip keys={["improvement.effectiveness_days"]} />
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {canPlan && (
            <button onClick={() => setProgrammeForm({ programme: null })} style={{ ...secondaryBtn, display: "flex", alignItems: "center", gap: 6 }}>
              <Target size={14} /> New programme
            </button>
          )}
          {canEdit && (
            <button onClick={() => { setViewingId(null); open(null); }} style={{ ...btnPrimary, display: "flex", alignItems: "center", gap: 6 }}>
              <Plus size={15} /> Raise an action
            </button>
          )}
        </div>
      </div>

      {error && !modalOpen && <div style={{ ...card, marginBottom: 16, borderLeft: `3px solid ${theme.color.danger}`, color: theme.color.danger, fontSize: text.body.fontSize }}>{error}</div>}
      {notice && !modalOpen && <div style={{ ...card, marginBottom: 16, borderLeft: `3px solid ${theme.color.accent}`, ...text.body, color: theme.color.subtle }}>{notice}</div>}

      <AutomaticSourcesPanel canChange={canPlan} onRan={load} />

      <SummaryTiles tiles={[
        { key: "open", label: "Open", value: counts.open, hint: "Not yet verified or cancelled", ...tile("open") },
        { key: "overdue", label: "Overdue", value: counts.overdue, tone: counts.overdue ? "danger" : "neutral", ...tile("overdue") },
        { key: "verification", label: "Awaiting verification", value: counts.verification, tone: counts.verification ? "accent" : "neutral", hint: "Completed; someone other than the owner should confirm it worked", ...tile("verification") },
        { key: "effectiveness", label: "Effectiveness reviews due", value: counts.effectiveness, tone: counts.effectiveness ? "warning" : "neutral", hint: "Verified work to check it held", ...tile("effectiveness") },
        { key: "blocked", label: "Blocked", value: counts.blocked, tone: counts.blocked ? "warning" : "neutral", ...tile("blocked") },
        { key: "aged", label: "Median age of open actions", value: `${median} d`, hint: `${counts.aged} open longer than ${AGED_DAYS} days`, tone: counts.aged ? "warning" : "neutral", ...tile("aged") },
      ]} />

      {programmes.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 12, marginBottom: 20 }}>
          {programmes.map((p) => (
            <div key={p.id} data-deeplink={p.id} style={{ ...card, borderLeft: `3px solid ${p.health.behindSchedule ? theme.color.warning : theme.color.accent}` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                <Target size={14} color={theme.color.accent} />
                <span style={{ ...text.itemTitle, flex: 1 }}>{p.name}</span>
                {p.status !== "active" && <StatusPill value={p.status} label={words(p.status)} />}
                {canPlan && <EditButton onClick={() => setProgrammeForm({ programme: p })} />}
              </div>
              <div style={text.stat}>{p.health.completionPct}%</div>
              <div style={{ ...text.meta, color: theme.color.subtle }}>
                verified complete{p.health.elapsedPct !== null && <> · {p.health.elapsedPct}% of the time elapsed</>}
              </div>
              {p.health.behindSchedule && (
                <div style={{ ...text.meta, color: theme.color.warning, marginTop: 6, fontWeight: 600 }}>
                  Behind schedule — {p.health.open} still open{p.health.blocked ? `, ${p.health.blocked} blocked` : ""}
                </div>
              )}
              <div style={{ ...text.meta, marginTop: 6 }}>
                {p.health.verified} verified of {p.health.total}
                {p.health.effortDaysRemaining > 0 && <> · {p.health.effortDaysRemaining} days of effort remaining</>}
              </div>
            </div>
          ))}
        </div>
      )}

      <RegisterToolbar<Action>
        search={search} onSearch={setSearch} placeholder="Search actions, owners, causes"
        sortOptions={SORTS} sort={sort} onSort={setSort}
        onExport={exportCsv}
        count={{ shown: shown.length, total: actions.length }}
      >
        {([
          ["attention", `Needs attention (${counts.attention})`],
          ["open", `Open (${counts.open})`],
          ["all", `All (${actions.length})`],
        ] as const).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setView(key)}
            aria-pressed={view === key}
            style={{
              ...secondaryBtn,
              background: view === key ? theme.color.accent : "transparent",
              color: view === key ? btnPrimary.color : theme.color.subtle,
              borderColor: view === key ? theme.color.accent : theme.color.border,
            }}
          >
            {label}
          </button>
        ))}
        <EntityTagFilters value={tagFilter} onChange={setTagFilter} />
        <div role="group" aria-label="Layout" style={{ display: "flex" }}>
          {(["list", "board"] as const).map((l, i) => (
            <button
              key={l}
              type="button"
              onClick={() => setLayout(l)}
              aria-pressed={layout === l}
              style={{
                ...secondaryBtn,
                borderRadius: i === 0 ? "3px 0 0 3px" : "0 3px 3px 0",
                background: layout === l ? theme.color.accentBg : "transparent",
                color: layout === l ? theme.color.accentText : theme.color.subtle,
              }}
            >
              {l === "list" ? "List" : "Board"}
            </button>
          ))}
        </div>
      </RegisterToolbar>

      {shown.length === 0 ? (
        <EmptyState
          title={view === "attention" ? "Nothing needs attention" : actions.length ? "No actions match" : "No actions yet"}
          detail={view === "attention"
            ? "Nothing is overdue, blocked or unowned. That is worth saying plainly rather than hiding behind an empty list."
            : actions.length ? "Clear the search or pick another tile to see more." : "Raise one here, or let a finding, incident lesson or maturity shortfall raise one for you."}
        />
      ) : layout === "board" ? (
        <ImprovementBoard
          actions={shown}
          statuses={catalogue.statuses}
          transitions={catalogue.transitions}
          onOpen={(a) => show(a)}
          onMove={(a, to) => void move(a, to)}
          badge={healthBadge}
          busyId={busyId}
          canMove={canEdit}
        />
      ) : (
        <div style={{ ...card, padding: 0, overflow: "hidden" }}>
          {shown.map((a, i) => (
            <div key={a.id} style={{ borderTop: i === 0 ? "none" : `1px solid ${theme.color.border}` }}>
            <button
              onClick={() => show(a)}
              style={{
                display: "block", width: "100%", textAlign: "left", background: "transparent",
                border: "none", padding: "12px 16px 4px", cursor: "pointer",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                {healthBadge(a)}
                {a.displayId !== null && <span style={{ ...text.ref, color: theme.color.faint }}>CA-{a.displayId}</span>}
                <span style={{ ...text.itemTitle, flex: 1 }}>{a.title}</span>
                {reviewDue(a) && (
                  <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 4, background: theme.color.warningBg, color: theme.color.warningText }}>
                    {a.effectiveness === "overdue" ? "Review overdue" : "Review due"}
                  </span>
                )}
                {(() => { const t = tickets.linkFor(a.id); return t ? <TicketChip link={t} compact /> : null; })()}
                <StatusPill value={a.status} />
              </div>
              <div style={{ ...text.hint, marginTop: 4 }}>
                {sourceLabel(a.source)}
                {a.ownerName && <> · {a.ownerName}</>}
                {a.dueDate && <> · due {fmtDateOnly(a.dueDate)}</>}
                {isOpen(a) && <> · open {ageDays(a)} days</>}
                {a.milestones.length > 0 && <> · {a.milestones.filter((m) => m.completedAt).length}/{a.milestones.length} milestones</>}
                {a.priority !== "medium" && <> · {words(a.priority)} priority</>}
                {a.rootCauseRequired && !a.rootCause && isOpen(a) && <> · root cause not recorded</>}
                {/* The row it fixes, as a link rather than a reference string. */}
                {a.risk && <> · {a.risk.displayId ? `RISK-${a.risk.displayId}` : a.risk.title}</>}
                {a.incident && <> · {a.incident.displayId ?? a.incident.title}</>}
                {a.supplier && <> · {a.supplier.name}</>}
                {a.asset && <> · {a.asset.name}</>}
              </div>
            </button>
            <div style={{ padding: "0 16px 10px" }}>
              <EntityTagsRow entity={{ kind: "action", id: a.id }} readOnly style={{ marginBottom: 0 }} />
            </div>
            </div>
          ))}
        </div>
      )}

      {viewing && (() => {
        const v = viewing;
        const statusLabel = (s: string) => catalogue.statuses.find((x) => x.id === s)?.label ?? words(s);
        const moves = catalogue.transitions[v.status] ?? [];
        const programme = programmes.find((p) => p.id === v.programmeId);
        const doneMilestones = v.milestones.filter((m) => m.completedAt).length;
        const recordable = canRecordEffectiveness(v);
        const canEditThis = canWriteAction(perms, { source: v.source, riskId: v.risk?.id, supplierId: v.supplier?.id, incidentId: v.incident?.id });
        const close = () => { setViewingId(null); setRecordingReview(false); setError(""); };
        return (
          <ModalOverlay onClose={close} wide>
            <ModalHeader
              title={`${v.displayId !== null ? `CA-${v.displayId} · ` : ""}${v.title}`}
              onClose={close}
              right={canEditThis ? <EditButton onClick={() => open(v)} /> : undefined}
            />
            <div style={{ padding: 20 }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
                {healthBadge(v)}
                <StatusPill value={v.status} label={statusLabel(v.status)} />
                <TicketControl kind="corrective_action" subjectRef={v.id} links={tickets} canRaise={canPlan} />
                {reviewDue(v) && (
                  <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 4, background: theme.color.warningBg, color: theme.color.warningText }}>
                    {v.effectiveness === "overdue" ? "Review overdue" : "Review due"}
                  </span>
                )}
              </div>

              <NextSteps items={actionSteps(v, formOf(v), sourceLabel(v.source))} />

              <DetailSection title="Details">
                <DetailFields fields={[
                  { label: "Description", value: v.description, wide: true, multiline: true },
                  { label: "Raised by", value: sourceLabel(v.source) },
                  { label: "Priority", value: words(v.priority) },
                  { label: "Owner", value: v.ownerName },
                  { label: "Due date", value: v.dueDate ? fmtDateOnly(v.dueDate) : null },
                  { label: "Effort (days)", value: v.effortDays ?? null },
                  { label: "Programme", value: programme?.name ?? null },
                  { label: "Control or clause", value: v.controlId },
                  { label: "Raised from", value: sourceRefLabel(v.sourceRef) },
                  ...(v.automatic ? [{ label: "Raised automatically", value: v.autoResolvedAt
                    ? `Yes. The failure cleared on ${fmtDay(v.autoResolvedAt)}, so the platform completed it; someone still has to verify it.`
                    : "Yes. It completes itself when the failure clears, and is never verified automatically.", wide: true }] : []),
                ]} />
              </DetailSection>

              <DetailSection title="Progress">
                <DetailFields fields={[
                  { label: "Raised", value: `${fmtDay(v.createdAt)}${isOpen(v) ? ` (open ${ageDays(v)} days)` : ""}` },
                  { label: "Started", value: v.startedAt ? fmtDay(v.startedAt) : null },
                  { label: "Completed", value: v.completedAt ? fmtDay(v.completedAt) : null },
                  { label: "Verified", value: v.verifiedAt ? fmtDay(v.verifiedAt) : null },
                  ...(v.status === "blocked" || v.blockedReason ? [{ label: "Why it is blocked", value: v.blockedReason, wide: true, multiline: true }] : []),
                  ...(v.status === "verified" || v.verificationNote ? [{ label: "How it was verified", value: v.verificationNote ?? null, wide: true, multiline: true }] : []),
                ]} />
                {(v.milestones.length > 0 || canEditThis) && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ ...text.label, marginBottom: 4 }}>Milestones ({doneMilestones}/{v.milestones.length})</div>
                    <MilestonesEditor actionId={v.id} milestones={v.milestones} canEdit={canEditThis} onChanged={load} />
                  </div>
                )}
              </DetailSection>

              <DetailSection title="Root cause">
                <RootCauseView
                  value={{ method: v.rootCauseMethod ?? "", text: v.rootCause ?? "" }}
                  methods={catalogue.rootCauseMethods}
                  required={v.rootCauseRequired}
                  aiDraftAt={v.rootCauseAiDraftAt ?? null}
                />
              </DetailSection>

              {hasEffectivenessReview(v) && (
                <DetailSection title="Effectiveness review">
                  <EffectivenessView action={v} />
                </DetailSection>
              )}

              <DetailSection title="Linked records">
                <EntityTagsRow entity={{ kind: "action", id: v.id }} />
                <RelatedRecords entity={{ kind: "action", id: v.id }} links={actionRecordLinks(v)} />
              </DetailSection>

              {notice && <div style={{ ...text.hint, color: theme.color.accentText, margin: "4px 0 10px" }}>{notice}</div>}
              {error && <div style={{ ...text.hint, color: theme.color.danger, margin: "4px 0 10px" }}>{error}</div>}

              {canEditThis && (moves.length > 0 || recordable) && (
                <RecordActions note={moves.length > 0 ? "Only the moves the lifecycle allows from where it is. Blocking, verifying, or completing without a root cause asks for the detail first." : undefined}>
                  {moves.map((to) => (
                    <button key={to} type="button" onClick={() => void move(v, to)} disabled={busyId === v.id} style={{ ...secondaryBtn, opacity: busyId === v.id ? 0.6 : 1 }}>
                      Move to {statusLabel(to).toLowerCase()}
                    </button>
                  ))}
                  {recordable && !recordingReview && (
                    <button type="button" onClick={() => setRecordingReview(true)} style={secondaryBtn}>Record effectiveness review</button>
                  )}
                </RecordActions>
              )}

              {canEditThis && recordable && recordingReview && (
                <ActionEffectiveness
                  action={v}
                  onCancel={() => setRecordingReview(false)}
                  onDone={async (followUpId) => {
                    setRecordingReview(false);
                    await load();
                    setNotice(followUpId ? "Effectiveness recorded, and a follow-up action raised." : "Effectiveness recorded.");
                  }}
                />
              )}
            </div>
          </ModalOverlay>
        );
      })()}

      {programmeForm && (
        <ProgrammeModal
          programme={programmeForm.programme}
          onClose={() => setProgrammeForm(null)}
          onSaved={async (message) => { setProgrammeForm(null); setNotice(message); await load(); }}
        />
      )}

      {editing && (
        <ModalOverlay onClose={() => setEditing(null)} wide>
          <ModalHeader
            title={current ? `${current.displayId ? `CA-${current.displayId} · ` : ""}Edit action` : "Raise an action"}
            onClose={() => setEditing(null)}
          />
          <div style={{ padding: 20 }}>
            {current && <NextSteps items={actionSteps(current, form, sourceLabel(current.source))} />}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 16px" }}>
              <Field label="Title" span2>
                <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} style={input} placeholder="What has to change" />
              </Field>
              <Field label="Description" span2>
                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} style={{ ...textarea, minHeight: 70 }} />
              </Field>
              <Field label="Raised by">
                <select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} style={input}>
                  {(catalogue.sources.length ? catalogue.sources : [{ id: form.source, label: words(form.source) }])
                    .filter((s) => s.id === form.source || canWriteAction(perms, { source: s.id, riskId: form.riskId, supplierId: form.supplierId, incidentId: form.incidentId }))
                    .map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
              </Field>
              <Field label="Priority">
                <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} style={input}>
                  {PRIORITIES.map((p) => <option key={p} value={p}>{words(p)}</option>)}
                </select>
              </Field>
              <Field label="Owner (member)" help="A member who owns it sees it in My tasks.">
                <select
                  value={form.ownerUserId}
                  onChange={(e) => {
                    const id = e.target.value;
                    const name = lookups?.members.find((m) => m.id === id)?.name;
                    setForm({ ...form, ownerUserId: id, ownerName: id && name ? name : form.ownerName });
                  }}
                  style={input}
                  disabled={!lookups}
                >
                  <option value="">{lookups ? "Not a member" : "Loading…"}</option>
                  {form.ownerUserId && !lookups?.members.some((m) => m.id === form.ownerUserId) && <option value={form.ownerUserId}>Current owner</option>}
                  {(lookups?.members ?? []).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </Field>
              <Field label="Owner name" help="An action nobody owns will not move, whatever its due date says.">
                <input value={form.ownerName} onChange={(e) => setForm({ ...form, ownerName: e.target.value })} style={input} />
              </Field>
              <Field label="Due date">
                <input type="date" value={form.dueDate} onChange={(e) => setForm({ ...form, dueDate: e.target.value })} style={input} />
              </Field>
              <Field label="Effort (days)">
                <input type="number" min={0} value={form.effortDays} onChange={(e) => setForm({ ...form, effortDays: e.target.value })} style={input} />
              </Field>
              <Field label="Programme">
                <select value={form.programmeId} onChange={(e) => setForm({ ...form, programmeId: e.target.value })} style={input}>
                  <option value="">None</option>
                  {programmes.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </Field>
              <div />
              <Field label="Control or clause" span2 help="Optional — links the action to what it serves in the SOA. Picked from the organisation's own standards, so the id matches what the SOA, the evidence pack and the tags are keyed by.">
                <ControlPicker
                  value={form.controlId || null}
                  frameworkId={form.frameworkId || null}
                  onChange={({ controlId, frameworkId }) => setForm({ ...form, controlId: controlId ?? "", frameworkId: frameworkId ?? "" })}
                />
              </Field>
              <div style={{ ...text.sectionTitle, gridColumn: "1 / -1", margin: "4px 0 0" }}>What it fixes</div>
              {([
                ["riskId", "Risk", lookups?.risks],
                ["incidentId", "Incident", lookups?.incidents],
                ["supplierId", "Supplier", lookups?.suppliers],
                ["assetId", "Asset", lookups?.assets],
              ] as Array<["riskId" | "incidentId" | "supplierId" | "assetId", string, LinkOption[] | undefined]>).map(([key, label, options]) => (
                <Field key={key} label={label}>
                  <select value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} style={input} disabled={!options}>
                    <option value="">{options ? "None" : "Loading…"}</option>
                    {/* Keep the current link selectable even if the list could not be read. */}
                    {form[key] && !options?.some((o) => o.id === form[key]) && <option value={form[key]}>Current link</option>}
                    {(options ?? []).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                  </select>
                </Field>
              ))}
              <Field
                label="Root cause"
                span2
                help={catalogue.rootCauseRequiredSources.includes(form.source)
                  ? "Why it happened, so the fix removes the cause rather than the symptom. Required before an action raised from this source can be completed (ISO 27001 10.2 b)."
                  : "Optional for this source. Why it happened, so the fix removes the cause rather than the symptom."}
              >
                <ActionRootCause
                  value={{ method: form.rootCauseMethod, text: form.rootCause }}
                  onChange={(v) => setForm((f) => ({ ...f, rootCauseMethod: v.method, rootCause: v.text }))}
                  methods={catalogue.rootCauseMethods}
                  required={catalogue.rootCauseRequiredSources.includes(form.source)}
                />
                {current && current.rootCauseRequired && !["verified", "cancelled"].includes(current.status) && (
                  <RootCauseDraftPanel
                    actionId={current.id}
                    method={form.rootCauseMethod}
                    hasText={!!form.rootCause.trim()}
                    onUse={(v) => setForm((f) => ({ ...f, rootCauseMethod: v.method, rootCause: v.text, rootCauseFromAiDraft: true }))}
                  />
                )}
                {form.rootCauseFromAiDraft && form.rootCause.trim() && (
                  <div style={{ ...text.hint, marginTop: 6 }}>
                    Started from an AI draft. Check every line against what you know, change what is wrong, then save; the action will record that its root cause started from an AI draft.
                  </div>
                )}
              </Field>
              {current && (
                <>
                  <Field label="Status" help="Only the moves the lifecycle allows from where it is. Verification is a separate step: only a completed action can be verified.">
                    <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} style={input}>
                      {statusOptions.map((s) => <option key={s} value={s}>{catalogue.statuses.find((x) => x.id === s)?.label ?? words(s)}</option>)}
                    </select>
                  </Field>
                  {form.status === "verified" && current.status !== "verified" ? (
                    <Field label="Review effectiveness after" help="When to check the fix held (ISO 27001 10.2 d).">
                      <select value={form.effectivenessReviewDays} onChange={(e) => setForm({ ...form, effectivenessReviewDays: e.target.value })} style={input}>
                        {catalogue.effectivenessReviewOptions.map((d) => <option key={d} value={String(d)}>{d} days{d === catalogue.defaultEffectivenessDays ? " (default)" : ""}</option>)}
                      </select>
                    </Field>
                  ) : <div />}
                  {form.status === "blocked" && (
                    <Field label="Why it is blocked" span2>
                      <textarea value={form.blockedReason} onChange={(e) => setForm({ ...form, blockedReason: e.target.value })} style={textarea} />
                    </Field>
                  )}
                  {form.status === "verified" && (
                    <Field label="How it was verified" span2 help={current.status === "completed" ? "Ideally by someone other than the person who did the work." : undefined}>
                      <textarea required value={form.verificationNote} onChange={(e) => setForm({ ...form, verificationNote: e.target.value })} style={textarea} />
                    </Field>
                  )}
                </>
              )}
            </div>

            {error &&<div style={{ ...text.hint, color: theme.color.danger, marginTop: 10 }}>{error}</div>}

            <div style={{ display: "flex", gap: 8, justifyContent: "space-between", marginTop: 16 }}>
              <div>
                {current && current.status !== "verified" && (
                  <button onClick={() => remove(current)} style={dangerBtn}>Delete</button>
                )}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => setEditing(null)} style={secondaryBtn}>Cancel</button>
                <button onClick={save} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}>
                  {saving ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          </div>
        </ModalOverlay>
      )}
    </div>
  );
}

/**
 * An action's milestones: ticked off, edited, added and removed in place. Who may is the same
 * rule as the action itself (the server checks the action's row); each change is audited there.
 */
/**
 * Which failures raise corrective actions by themselves (backend lib/findingActions.ts): failed CIS
 * rules, denied or undeclared zone flows, high-severity firewall findings. Each can be switched off;
 * switching one off stops raising and resolving, it never deletes what was raised.
 */
function AutomaticSourcesPanel({ canChange, onRan }: { canChange: boolean; onRan: () => void }) {
  const [sources, setSources] = useState<AutomaticSource[] | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    api.get<{ sources: AutomaticSource[] }>("/improvement/automatic").then((d) => setSources(d.sources)).catch(() => setSources(null));
  }, []);
  if (!sources) return null;
  const on = sources.filter((s) => s.enabled).length;

  async function toggle(id: string, enabled: boolean) {
    if (!sources) return;
    setBusy(true);
    setMessage(null);
    try {
      const disabledSources = sources.filter((s) => (s.id === id ? !enabled : !s.enabled)).map((s) => s.id);
      const d = await api.patch<{ sources: AutomaticSource[] }>("/improvement/automatic", { disabledSources });
      setSources(d.sources);
    } catch (e) {
      setMessage(e instanceof Error ? e.message : "Could not save the change.");
    } finally {
      setBusy(false);
    }
  }

  async function runNow() {
    setBusy(true);
    setMessage(null);
    try {
      const r = await api.post<{ created: number; completed: number; reopened: number }>("/improvement/automatic/run", {});
      setMessage(`${r.created} raised, ${r.completed} completed pending verification, ${r.reopened} reopened.`);
      onRan();
    } catch (e) {
      setMessage(e instanceof Error ? e.message : "The check did not run.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ ...card, padding: "10px 14px", marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <span style={{ ...text.itemTitle, flex: 1 }}>Raised automatically from failures</span>
        <span style={text.hint}>{on} of {sources.length} sources on</span>
        <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} style={{ ...btnSecondary, padding: "5px 10px", fontSize: text.hint.fontSize }}>
          {open ? "Hide" : "Show"}
        </button>
      </div>
      {open && (
        <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
          <div style={text.hint}>
            Each failing item raises one proposed action, linked to the controls it bears on. When the failure clears the action moves to
            completed, awaiting verification. It is never verified automatically: someone other than the owner should confirm the fix.
          </div>
          {sources.map((s) => (
            <label key={s.id} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "6px 0", borderTop: `1px solid ${theme.color.border}`, cursor: canChange ? "pointer" : "default" }}>
              <input type="checkbox" checked={s.enabled} disabled={!canChange || busy} onChange={(e) => toggle(s.id, e.target.checked)} style={{ marginTop: 3 }} />
              <span>
                <span style={{ ...text.body, fontWeight: 600, display: "block" }}>{s.label}</span>
                <span style={text.hint}>{s.description}</span>
              </span>
            </label>
          ))}
          {canChange && (
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <button type="button" onClick={runNow} disabled={busy || on === 0} style={{ ...btnSecondary, padding: "6px 12px", fontSize: text.hint.fontSize, opacity: busy || on === 0 ? 0.6 : 1 }}>
                {busy ? "Checking…" : "Check now"}
              </button>
              <span style={text.hint}>Also runs after each change to a benchmark rule, zone, flow or firewall snapshot, and daily.</span>
            </div>
          )}
          {message && <div style={text.hint}>{message}</div>}
        </div>
      )}
    </div>
  );
}

function MilestonesEditor({ actionId, milestones, canEdit, onChanged }: {
  actionId: string; milestones: Milestone[]; canEdit: boolean; onChanged: () => Promise<void>;
}) {
  // "new" for the add form, a milestone id for its edit form.
  const [formFor, setFormFor] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [due, setDue] = useState("");
  const [owner, setOwner] = useState("");
  const [members, setMembers] = useState<Array<{ id: string; name: string }> | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function run(fn: () => Promise<unknown>, fallback: string) {
    setBusy(true); setError("");
    try { await fn(); setFormFor(null); await onChanged(); }
    catch (e) { setError(apiErrorMessage(e, fallback)); }
    finally { setBusy(false); }
  }
  function startForm(m: Milestone | null) {
    setFormFor(m ? m.id : "new"); setTitle(m?.title ?? ""); setDue(toDateInput(m?.dueDate)); setOwner(m?.ownerUserId ?? ""); setError("");
    if (!members) {
      api.get<{ members: Array<{ id: string; displayName: string | null; email: string; isActive?: boolean }> }>("/organizations/members")
        .then((d) => setMembers(d.members.filter((x) => x.isActive !== false).map((x) => ({ id: x.id, name: x.displayName || x.email }))))
        .catch(() => setMembers([]));
    }
  }
  function saveForm() {
    if (!title.trim()) { setError("Give the milestone a title."); return; }
    const body = { title: title.trim(), dueDate: fromInput(due), ownerUserId: owner || null };
    void run(() => (formFor === "new"
      ? api.post(`/improvement/actions/${actionId}/milestones`, { ...body, position: milestones.length })
      : api.put(`/improvement/milestones/${formFor}`, body)), "Could not save the milestone.");
  }
  const linkBtn: React.CSSProperties = { background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: text.meta.fontSize };
  const form = (
    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", margin: "6px 0" }}>
      <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Milestone" maxLength={300} style={{ ...input, flex: "1 1 220px" }} />
      <input type="date" value={due} onChange={(e) => setDue(e.target.value)} style={{ ...input, width: "auto" }} aria-label="Due date" />
      <select value={owner} onChange={(e) => setOwner(e.target.value)} style={{ ...input, width: "auto", maxWidth: 220 }} aria-label="Given to" disabled={!members}>
        <option value="">Not given to anyone</option>
        {owner && members && !members.some((m) => m.id === owner) && <option value={owner}>Current person</option>}
        {(members ?? []).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
      </select>
      <button type="button" onClick={saveForm} disabled={busy} style={{ ...btnPrimary, opacity: busy ? 0.6 : 1 }}>{busy ? "Saving…" : "Save"}</button>
      <button type="button" onClick={() => setFormFor(null)} disabled={busy} style={secondaryBtn}>Cancel</button>
    </div>
  );

  return (
    <div>
      {milestones.length === 0 && formFor !== "new" && <div style={text.hint}>No milestones yet. Break a long action into steps with their own dates.</div>}
      <div style={{ display: "grid", gap: 4 }}>
        {milestones.map((m) => formFor === m.id ? <div key={m.id}>{form}</div> : (
          <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 8, ...text.body }}>
            <input
              type="checkbox" checked={!!m.completedAt} disabled={!canEdit || busy}
              aria-label={m.completedAt ? `Mark "${m.title}" not done` : `Mark "${m.title}" done`}
              onChange={() => void run(() => api.put(`/improvement/milestones/${m.id}`, { completedAt: m.completedAt ? null : new Date().toISOString() }), "Could not update the milestone.")}
            />
            <span style={{ flex: 1, textDecoration: m.completedAt ? "line-through" : undefined, color: m.completedAt ? theme.color.muted : theme.color.text }}>
              {m.title}
              <span style={text.hint}>
                {m.owner ? ` · ${m.owner.displayName || m.owner.email}` : m.ownerName ? ` · ${m.ownerName} (not a member)` : ""}
                {m.completedAt ? ` · done ${fmtDay(m.completedAt)}` : m.dueDate ? ` · due ${fmtDateOnly(m.dueDate)}` : ""}
              </span>
            </span>
            {canEdit && formFor === null && (
              <>
                <button type="button" onClick={() => startForm(m)} style={{ ...linkBtn, color: theme.color.accentText }}>Edit</button>
                <button type="button" disabled={busy} onClick={() => { if (window.confirm(`Delete the milestone "${m.title}"?`)) void run(() => api.del(`/improvement/milestones/${m.id}`), "Could not delete the milestone."); }} style={{ ...linkBtn, color: theme.color.dangerText }}>Delete</button>
              </>
            )}
          </div>
        ))}
      </div>
      {formFor === "new" && form}
      {canEdit && formFor === null && (
        <button type="button" onClick={() => startForm(null)} style={{ ...linkBtn, color: theme.color.accentText, marginTop: 6 }}>+ Add milestone</button>
      )}
      {error && <div style={{ ...text.hint, color: theme.color.danger, marginTop: 6 }}>{error}</div>}
    </div>
  );
}

/** Create, edit or delete an improvement programme. Deleting keeps its actions (they lose the programme). */
function ProgrammeModal({ programme, onClose, onSaved }: {
  programme: Programme | null; onClose: () => void; onSaved: (message: string) => Promise<void>;
}) {
  const [f, setF] = useState({
    name: programme?.name ?? "", objective: programme?.objective ?? "", sponsorName: programme?.sponsorName ?? "",
    status: programme?.status ?? "planned", startDate: toDateInput(programme?.startDate), endDate: toDateInput(programme?.endDate),
    notes: programme?.notes ?? "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((p) => ({ ...p, [k]: v }));

  async function save() {
    if (!f.name.trim()) { setError("Give the programme a name."); return; }
    if (f.startDate && f.endDate && f.endDate < f.startDate) { setError("The end date cannot be before the start date."); return; }
    setBusy(true); setError("");
    // Every field is sent, so an emptied one is cleared rather than kept.
    const body = {
      name: f.name.trim(), objective: f.objective.trim() || null, sponsorName: f.sponsorName.trim() || null, status: f.status,
      startDate: fromInput(f.startDate), endDate: fromInput(f.endDate), notes: f.notes.trim() || null,
    };
    try {
      if (programme) await api.put(`/improvement/programmes/${programme.id}`, body);
      else await api.post("/improvement/programmes", body);
      await onSaved(programme ? `${body.name} saved.` : `${body.name} created. Assign actions to it from each action's form.`);
    } catch (e) { setError(apiErrorMessage(e, "Could not save the programme.")); setBusy(false); }
  }

  async function remove() {
    if (!programme) return;
    if (!window.confirm(`Delete the programme "${programme.name}"? Its ${programme.health.total} action(s) are kept and simply lose the programme.`)) return;
    setBusy(true); setError("");
    try {
      await api.del(`/improvement/programmes/${programme.id}`);
      await onSaved(`${programme.name} deleted; its actions are kept.`);
    } catch (e) { setError(apiErrorMessage(e, "Could not delete the programme.")); setBusy(false); }
  }

  return (
    <ModalOverlay onClose={onClose}>
      <ModalHeader title={programme ? `Edit ${programme.name}` : "New improvement programme"} onClose={onClose} />
      <div style={{ padding: 20, display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 16px" }}>
        <Field label="Name" span2><input value={f.name} onChange={(e) => set("name", e.target.value)} maxLength={200} style={input} /></Field>
        <Field label="Objective" span2><textarea value={f.objective} onChange={(e) => set("objective", e.target.value)} style={{ ...textarea, minHeight: 60 }} placeholder="What the programme is for, and how you will know it worked" /></Field>
        <Field label="Sponsor"><input value={f.sponsorName} onChange={(e) => set("sponsorName", e.target.value)} maxLength={200} style={input} /></Field>
        <Field label="Status">
          <select value={f.status} onChange={(e) => set("status", e.target.value)} style={input}>
            {PROGRAMME_STATUSES.map((s) => <option key={s} value={s}>{words(s)}</option>)}
          </select>
        </Field>
        <Field label="Start"><input type="date" value={f.startDate} onChange={(e) => set("startDate", e.target.value)} style={input} /></Field>
        <Field label="End" help="With a start date, the cards compare the share of time elapsed with the share of work verified."><input type="date" value={f.endDate} onChange={(e) => set("endDate", e.target.value)} style={input} /></Field>
        <Field label="Notes" span2><textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} style={{ ...textarea, minHeight: 50 }} /></Field>
        {error && <div style={{ ...text.hint, gridColumn: "1 / -1", color: theme.color.danger }}>{error}</div>}
        <div style={{ gridColumn: "1 / -1", display: "flex", gap: 8, justifyContent: "space-between" }}>
          <div>{programme && <button type="button" onClick={remove} disabled={busy} style={dangerBtn}>Delete</button>}</div>
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" onClick={onClose} disabled={busy} style={secondaryBtn}>Cancel</button>
            <button type="button" onClick={save} disabled={busy} style={{ ...btnPrimary, opacity: busy ? 0.6 : 1 }}>{busy ? "Saving…" : "Save"}</button>
          </div>
        </div>
      </div>
    </ModalOverlay>
  );
}
